import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { UAParser } from "ua-parser-js";
import { useTranslate } from "../../../../language/i18n";
import { setError } from "../../../../reducers/dialog/dialogAction";
import { RootState } from "../../../../reducers/rootReducer";
import useAvailableLoginMethods, { UI_LOGIN_METHODS } from "../../../Start/Welcome/hooks/useAvailableLoginMethods";
import { PENDING, UNKNOWN, USER_SIGN } from "../constants";
import { useBankIDCollect, useBankIdStartAuthentication, useQRCode } from "./useBankIdAuthentication";

export const detectBrowser = () => {
  const browserName = new UAParser().getBrowser().name;

  if (browserName?.includes("Firefox")) {
    return "firefox";
  } else if (browserName?.includes("Chrome")) {
    return "googlechrome";
  }

  return UNKNOWN;
};

export const useBankId = () => {
  const [bankIdError, setBankIdError] = useState<string | undefined>(undefined);
  const [cancelled, setCancelled] = useState<boolean>(false);
  const [bankIdUrl, setBankIdUrl] = useState<string | undefined>(undefined);

  const { autoStartToken, handleAuth, bankIdContent, resetAuth, resetBankIdContent } = useBankIdStartAuthentication(
    setBankIdError,
  );

  const { qrCodeContent, stopQRGeneration } = useQRCode(bankIdContent);
  const { collectedResponse, resetCollect } = useBankIDCollect(bankIdContent, setBankIdError);
  const sessionId = useSelector((state: RootState) => state.session.sessionId);
  const { isAllowedLoginMethod, loginMethodsInitialized } = useAvailableLoginMethods();
  const translate = useTranslate();

  const reset = useCallback(() => {
    resetBankIdContent();
    resetCollect();
    stopQRGeneration();
    setCancelled(false);
  }, [resetBankIdContent, resetCollect, stopQRGeneration]);

  const restart = () => {
    reset();
    resetAuth();
  };

  const resetBankIdUrl = () => {
    setBankIdUrl(undefined);
  };

  useEffect(() => {
    if (sessionId) {
      setCancelled(true);
    }
  }, [sessionId]);

  useEffect(() => {
    if (bankIdError) {
      reset();
    }
  }, [bankIdError, reset]);

  useEffect(() => {
    if (!autoStartToken) return;

    setBankIdUrl(
      "bankid:///?autostarttoken=" +
        autoStartToken +
        (detectBrowser() === UNKNOWN ? "" : "&redirect=" + detectBrowser() + encodeURIComponent("://")),
    );
  }, [autoStartToken]);

  useEffect(() => {
    if (!collectedResponse) return;

    if (collectedResponse.hintCode === USER_SIGN || collectedResponse.status !== PENDING) {
      stopQRGeneration();
    }
  }, [collectedResponse, stopQRGeneration]);

  const initAuth = () => {
    if (!isAllowedLoginMethod(UI_LOGIN_METHODS.BANKID)) {
      setError({ error: "exception", errorMessage: translate("BANKID_IS_NOT_AN_AVAILABLE_LOGIN_METHOD") });
      setBankIdError("BANKID_IS_NOT_AN_AVAILABLE_LOGIN_METHOD");

      return;
    }

    if (bankIdError) {
      setBankIdError(undefined);
    }

    setBankIdUrl(undefined);
    handleAuth();
  };

  const isBankIdError = () => !!bankIdError;

  return {
    autoStartToken,
    bankIdContent,
    bankIdUrl,
    cancelled,
    collectedResponse,
    initAuth,
    isBankIdError,
    qrCodeContent,
    reset,
    resetBankIdUrl,
    restart,
    loginMethodsInitialized,
  };
};
